import { selectUser } from '@us/web/auth/data-access';
import { AsyncPipe, NgIf } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, Signal } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { IonicModule } from '@ionic/angular';
import { TranslocoModule } from '@ngneat/transloco';
import { LetDirective } from '@ngrx/component';
import { Store } from '@ngrx/store';
import { TuiSheetModule } from '@taiga-ui/addon-mobile';
import { TuiDestroyService, tuiIsPresent } from '@taiga-ui/cdk';
import { TuiDialogModule, TuiErrorModule, TuiSvgModule } from '@taiga-ui/core';
import { TuiCountryIsoCode } from '@taiga-ui/i18n';
import {
    TuiFieldErrorPipeModule,
    TuiInputModule,
    TuiInputPhoneInternationalModule,
    TuiSortCountriesPipeModule
} from '@taiga-ui/kit';
import {
    CartActions,
    selectCartBookingTime,
    selectCartReceiver,
    selectCartShop,
    selectOrderType
} from '@us/web/cart/data-access';
import { DeliveryAddressStore } from '@us/web/delivery-addresses/data-access';
import { selectGeocoderResult } from '@us/web/position/data-access';
import { DeliveryAddress, FormattedAddressPipe, OrderType, User } from '@us/web/shared/data-access';
import { provideCountriesMasks } from '@us/web/shared/feature';
import { SelectTimeComponent, SelectTimeV2Component } from '@us/web/shared/ui';
import { PHONE_NUMBER_PATTERN } from '@us/web/shared/utils';
import { formatISO } from 'date-fns';
import { FormatPipeModule, ParseIsoPipeModule } from 'ngx-date-fns';
import { BehaviorSubject, debounceTime, distinctUntilChanged, filter, skip, takeUntil, tap } from 'rxjs';
import { AddressInputAutocompleteComponent } from '@us/web/shops/feature';
import { selectAllowShopDelivery, selectPhoneAreaCode, selectTurnOffAddress } from '@us/web/shop/data-access';

@Component({
    selector: 'us-shipment-details',
    standalone: true,
    imports: [
        TranslocoModule,
        ReactiveFormsModule,
        TuiInputModule,
        TuiErrorModule,
        TuiFieldErrorPipeModule,
        AsyncPipe,
        TuiInputPhoneInternationalModule,
        TuiSortCountriesPipeModule,
        NgIf,
        AddressInputAutocompleteComponent,
        FormatPipeModule,
        FormattedAddressPipe,
        IonicModule,
        ParseIsoPipeModule,
        TuiSvgModule,
        LetDirective,
        SelectTimeComponent,
        TuiSheetModule,
        TuiDialogModule,
        SelectTimeV2Component,
        AddressInputAutocompleteComponent
    ],
    providers: [provideCountriesMasks, DeliveryAddressStore, TuiDestroyService],
    templateUrl: './shipment-details.component.html',
    styleUrls: ['./shipment-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ShipmentDetailsComponent implements OnInit {
    private readonly formBuilder = inject(FormBuilder);
    private readonly store = inject(Store);
    private readonly destroy$ = inject(TuiDestroyService, { self: true });

    readonly orderReceiver = this.store.selectSignal(selectCartReceiver);
    readonly orderType$ = this.store.select(selectOrderType);
    readonly shop$ = this.store.select(selectCartShop);
    readonly bookingTime$ = this.store.select(selectCartBookingTime);
    readonly phoneAreaCode$ = this.store.select(selectPhoneAreaCode);
    protected allowShopDelivery: Signal<boolean> = this.store.selectSignal(selectAllowShopDelivery);
    protected turnOffAddress: Signal<boolean> = this.store.selectSignal(selectTurnOffAddress);
    protected user: Signal<User | null> = this.store.selectSignal(selectUser);

    readonly form: FormGroup;
    readonly OrderType = OrderType;
    readonly countries: TuiCountryIsoCode[] = Object.values(TuiCountryIsoCode);
    readonly openPickupTimeSheet$ = new BehaviorSubject(false);
    readonly now = new Date();
    countryIsoCode = TuiCountryIsoCode.VN;

    constructor() {
        this.form = this.formBuilder.group({
            name: [null, [Validators.required]],
            phone: [null, [Validators.required, Validators.pattern(PHONE_NUMBER_PATTERN)]],
            address: [null, Validators.required],
            lat: [null],
            lng: [null]
        });
    }

    ngOnInit(): void {
        const receiver = this.orderReceiver();
        if (receiver) {
            const { name, phone, address } = receiver;
            this.form.patchValue({
                name: name ?? this.user()?.name ?? null,
                phone: phone ?? this.user()?.phone ?? null,
                address
            });
        }

        this.form.valueChanges
            .pipe(debounceTime(600), distinctUntilChanged(), takeUntil(this.destroy$))
            .subscribe(() => this.onChangeData());

        this.store
            .select(selectGeocoderResult)
            .pipe(
                takeUntil(this.destroy$),
                filter(tuiIsPresent),
                tap(gecode => {
                    this.form.patchValue({
                        address: gecode.formatted_address,
                        lat: gecode.geometry.location.lat,
                        lng: gecode.geometry.location.lng
                    });
                })
            )
            .subscribe();

        this.form.controls['phone']?.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                filter(tuiIsPresent),
                tap(phoneNumber => {
                    if (phoneNumber.startsWith('+84')) {
                        this.form.controls['phone'].setValidators([
                            Validators.required,
                            Validators.pattern(PHONE_NUMBER_PATTERN)
                        ]);
                    } else {
                        this.form.controls['phone'].setValidators([Validators.required]);
                    }
                    if (phoneNumber.startsWith('+840')) {
                        this.form.controls['phone'].setValue(phoneNumber.replace('+840', ''));
                    }
                })
            )
            .subscribe();

        this.phoneAreaCode$
            .pipe(
                takeUntil(this.destroy$),
                filter(tuiIsPresent),
                tap(phoneAreaCode => {
                    if(phoneAreaCode) {
                        this.countryIsoCode = phoneAreaCode as TuiCountryIsoCode;
                        if (phoneAreaCode === TuiCountryIsoCode.VN) {
                            this.form.controls['phone'].setValidators([
                                Validators.required,
                                Validators.pattern(PHONE_NUMBER_PATTERN)
                            ]);
                        } else {
                            this.form.controls['phone'].setValidators([Validators.required]);
                        }
                    }
                })
            )
            .subscribe();
        // this.getDistricts(true);
    }
    onChangeData() {
        const { name, phone, address, lat, lng } = this.form.getRawValue();

        const receiver: DeliveryAddress = {
            name,
            phone,
            address,
            latitude: lat,
            longitude: lng
        };

        this.store.dispatch(CartActions.updateCart({ receiver }));
    }

    showPickupTimeDialog() {
        this.openPickupTimeSheet$.next(true);
    }

    handleSelectPickupTime(bookingTime: Date) {
        this.store.dispatch(CartActions.updateCart({ booking_time: formatISO(bookingTime) }));
        this.openPickupTimeSheet$.next(false);
    }
}
